import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import NotFound from "../NotFound/404";
import { useSEO } from "../../context/SEOContext";
import ProfileMenu from "../../components/ProfileMenu/ProfileMenu";
import FormPhoneInput from "../../components/FormPhoneInput/FormPhoneInput";
import FormLocationInput from "../../components/FormLocationInput/FormLocationInput";
import ProfileBanner from "../../assets/profilebanner.png";
import "./Profile.css";
import { useTranslation } from "react-i18next";
const Profile = () => {
  const { t } = useTranslation();
  const setSeo = useSEO();
  useEffect(() => {
    setSeo({
      title: t("profile.meta.title"),
      description: t("profile.meta.description"),
      keywords: t("profile.meta.keywords"),
      favicon: "/faviconnew.png",
    });
  }, [setSeo, t]);

  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [isEditable, setIsEditable] = useState(false); // Düzenleme modunu kontrol eder
  const navigate = useNavigate();
  const userId = sessionStorage.getItem("user_id");

  useEffect(() => {
    if (!userId || userId !== id) {
      // ID eşleşmiyorsa yönlendir
      Swal.fire({
        icon: "warning",
        title: "Unauthorized Access!",
        text: "You do not have permission to access this profile!",
      }).then(() => {
        navigate(`/profile/${userId}`);
      });
      return;
    }

    const fetchUserData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users?id=${id}`
        );
        if (!response.ok) {
          throw new Error("User information could not be retrieved!");
        }
        const data = await response.json();
        setUser(data);
      } catch (error) {
        console.error("API Hatası:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "An error occurred while retrieving user information!",
        });
      }
    };

    fetchUserData();
  }, [id, navigate, userId]);

  if (user === null) {
    return (
      <div className="container">
        <div className="row">
          <div className="col-lg-12 my-5">
            <div className="alert alert-warning" role="alert">
              Loading your profile...
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (!user) {
    return <NotFound />;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUser((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleEditClick = async () => {
    const { value: password } = await Swal.fire({
      title: "Confirm Password",
      input: "password",
      inputLabel: "Enter your current password",
      inputPlaceholder: "Password",
      inputAttributes: {
        maxlength: 100,
        autocapitalize: "off",
        autocorrect: "off",
      },
      showCancelButton: true,
    });

    if (password) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/verifyPassword`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              email: user.email,
              password: password,
            }),
          }
        );

        const data = await response.json();

        if (response.ok && data.success) {
          setIsEditable(true); // Şifre doğruysa düzenleme modunu aç

          // Eski şifreyi düz haliyle inputa yaz
          setUser((prevUser) => ({
            ...prevUser,
            password: password,
          }));

          Swal.fire("Verified!", "You can now edit your profile.", "success");
        } else {
          Swal.fire("Access Denied", "Incorrect password", "error");
        }
      } catch (error) {
        Swal.fire("Error", "Server error. Please try again.", "error");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(user),
      });

      const data = await response.json();

      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: "Profile Updated!",
          text: "Your profile information has been successfully updated.",
        }).then(() => {
          setIsEditable(false); // Düzenleme modunu kapat
          window.location.reload(); // 1 saniye sonra sayfa yenilenir
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: data.error || "Login failed!",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Server Error!",
        text: "Something went wrong. Please try again!",
      });
    }
  };

  const handleLogout = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      if (response.ok && data.message === "Çıkış başarılı.") {
        sessionStorage.removeItem("user_id"); // user_id'yi temizle
        Swal.fire({
          icon: "success",
          title: "Logout Successful!",
          text: "You have logged out securely!",
        }).then(() => {
          window.location.href = "/userlogin"; // Sayfayı yenileyerek login sayfasına yönlendir
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: data.error || "Logout failed!",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Server Error!",
        text: "Something went wrong. Please try again!",
      });
    }
  };

  // Hesap sil (ID göndermeden)
  const handleDeleteAccount = async () => {
    if (!user) return;

    const ask = await Swal.fire({
      title: "Hesabı silmek istediğine emin misin?",
      text: "Bu işlem geri alınamaz.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Evet, sil",
      cancelButtonText: "Vazgeç",
    });
    if (!ask.isConfirmed) return;

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/users`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: Number(user.id) }),
      });

      const data = await res.json();
      if (!res.ok)
        throw new Error(data.error || data.message || "Hesap silinemedi.");

      await Swal.fire({
        icon: "success",
        title: "Hesap silindi",
        text: data.message || "Kullanıcı hesabın başarıyla silindi.",
      });

      // Oturumu temizle ve yönlendir
      sessionStorage.removeItem("user_id");
      window.location.href = "/userlogin";
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Hata",
        text: err.message || "Bir şeyler ters gitti. Lütfen tekrar dene.",
      });
    }
  };

  return (
    <>
      <ProfileMenu userId={user.id} />

      <div className="position-relative">
        <img
          src={ProfileBanner}
          alt="banner"
          style={{
            width: "100%",
            height: "375px",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />

        {user?.picture ? (
          <img
            src={user.picture}
            alt={user.name}
            style={{
              position: "absolute",
              bottom: "-30px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "90px",
              height: "90px",
              borderRadius: "8px",
              objectFit: "cover",
              border: "3px solid #fff",
              boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
            }}
          />
        ) : (
          <span
            className="rounded-2 d-flex align-items-center text-white justify-content-center fw-light fs-4"
            style={{
              position: "absolute",
              bottom: "-30px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "90px",
              height: "90px",
              backgroundColor: "var(--cherry-color)",
            }}
          >
            {user?.name
              ? `${user.name.split(" ")[0]?.[0]?.toUpperCase() || ""}.${
                  user.name.split(" ")[1]?.[0]?.toUpperCase() || ""
                }.`
              : ""}
          </span>
        )}
      </div>

      <div className="container">
        <div className="row">
          <div className="col-lg-12 py-5">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-lg-12 text-center mt-3">
                  <h5 className="mb-5 fw-light">{user.name}</h5>
                </div>
                <div className="col-lg-12 text-end mb-2">
                  {isEditable ? (
                    <button
                      type="submit"
                      className="btn profilebtn pe-5 px-5 p-2"
                    >
                      {t("ProfileUpdateButton")}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn profilebtn pe-5 px-5 p-2"
                      onClick={handleEditClick}
                    >
                      {t("EditProfileButton")}
                    </button>
                  )}
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="name" className="form-label">
                  {t("NameSurnameLabel")}
                </label>
                <input
                  type="text"
                  className="form-control p-3"
                  id="name"
                  name="name"
                  value={user.name}
                  onChange={handleInputChange}
                  readOnly={!isEditable}
                />
              </div>
              <div className="row">
                <div className="col-lg-6">
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label">
                      {t("EmailLabel")}
                    </label>
                    <input
                      type="email"
                      className="form-control p-3"
                      id="email"
                      name="email"
                      value={user.email}
                      onChange={handleInputChange}
                      readOnly={!isEditable}
                    />
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="mb-3">
                    <label htmlFor="password" className="form-label">
                      {t("PasswordLabel")}
                    </label>
                    <input
                      type="text"
                      className="form-control p-3"
                      id="password"
                      name="password"
                      value={user.password}
                      onChange={handleInputChange}
                      readOnly={!isEditable}
                    />
                  </div>
                </div>
                <div className="col-lg-12">
                  <div className="mb-3">
                    {/* <input
                      type="text"
                      className="form-control"
                      id="phone"
                      name="phone"
                      value={user.phone}
                      onChange={handleInputChange}
                      readOnly={!isEditable}
                    />
                    <FormPhoneInput /> */}

                    <FormPhoneInput
                      value={user.phone}
                      onChange={(phone) =>
                        handleInputChange({
                          target: {
                            name: "phone",
                            value: phone,
                          },
                        })
                      }
                      label={t("PhoneLabel")}
                      inputStyle={{
                        height: "55px",
                        width: "100%",
                        backgroundColor: isEditable ? "#fff" : "#E9ECEF",
                      }}
                      readOnly={!isEditable}
                    />
                  </div>
                </div>
              </div>
              <FormLocationInput
                user={user}
                handleInputChange={handleInputChange}
                isEditable={isEditable}
              />

              <div className="row">
                <div className="col-lg-6">
                  <div className="mb-3">
                    <label htmlFor="address" className="form-label">
                      {t("UpdateAddress1Label")}
                    </label>
                    <input
                      type="text"
                      className="form-control p-3"
                      id="address"
                      name="address1"
                      value={user.address1}
                      onChange={handleInputChange}
                      readOnly={!isEditable}
                    />
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="mb-3">
                    <label htmlFor="addresstwo" className="form-label">
                      {t("UpdateAddress2Label")}
                    </label>
                    <input
                      type="text"
                      className="form-control p-3"
                      id="addresstwo"
                      name="address2"
                      value={user.address2}
                      onChange={handleInputChange}
                      readOnly={!isEditable}
                    />
                  </div>
                </div>
              </div>
            </form>

            <div className="row">
              <div className="col-lg-12 text-end mt-3">
                <button
                  type="button"
                  className="btn btn-outline-dark p-2 pe-5 px-5 me-3"
                  onClick={handleDeleteAccount}
                >
                  {t("DeleteAccount")}
                </button>
                <button
                  type="button"
                  className="btn profilebtn p-2 pe-5 px-5 "
                  onClick={handleLogout}
                >
                  {t("SignOutButton")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Profile;
